import { useHistory, useParams } from "react-router-dom";
import { Card, PageContent, QueryResultHandler } from "src/components";
import { Css } from "src/Css";
import {
  JourneyPageProjectFragment,
  JourneyPageQuery,
  ProgressSummaryStatus,
  useJourneyPageQuery,
} from "src/generated/graphql-types";
import { milestoneRoute } from "src/routes";
import { ProgressBar } from "./components/ProgressBar";
import {
  SchedulePhaseOrPlanMilestone,
  getMilestoneProgressStatus,
  getStatusDisplay,
  getHomeownerConfig,
  getHomeownerMilestones,
  isStandaloneProject,
} from "./utils/milestoneUtils";

type MilestoneListRouteProps = {
  projectId: string;
};

export function MilestoneListPage() {
  const { projectId } = useParams<MilestoneListRouteProps>();
  const result = useJourneyPageQuery({
    variables: {
      projectId,
    },
  });

  return QueryResultHandler<JourneyPageQuery>({
    result,
    render: (data) => (
      <MilestoneListPageView
        homeownerProject={data.homeownerProject}
        projectId={projectId}
        isLegacy={isStandaloneProject(data.homeownerProject)}
      />
    ),
  });
}

type MilestoneListPageViewProps = {
  homeownerProject: JourneyPageProjectFragment;
  projectId: string;
  isLegacy: boolean;
};

export function MilestoneListPageView({ homeownerProject, projectId, isLegacy }: MilestoneListPageViewProps) {
  const history = useHistory();

  const milestones = getHomeownerMilestones(homeownerProject);
  const firstUnitNotStartedIndex = milestones.findIndex(
    (m) => getMilestoneProgressStatus(m) === ProgressSummaryStatus.NotStarted,
  );

  return (
    <>
      <PageContent>
        <div css={Css.mt2.$}>
          <h6 css={Css.header20.$}>Overall Construction Progress</h6>
          <div css={Css.mt4.$}>
            <ProgressBar
              title="Milestone Progress"
              completed={
                milestones.filter((m) => getMilestoneProgressStatus(m) === ProgressSummaryStatus.Complete).length
              }
              total={milestones.length}
            />
          </div>
          <div css={Css.mt4.relative.$}>
            <div css={Css.df.fdc.gap2.$}>
              {milestones.map((m, i) => (
                <MilestoneRow
                  key={m.id}
                  milestone={m}
                  firstNotStarted={i === firstUnitNotStartedIndex}
                  onClick={() => history.push(milestoneRoute(projectId, m.id), { from: "milestoneList" })}
                  isLegacy={isLegacy}
                />
              ))}
            </div>
            <div css={Css.absolute.top0.bottom0.left("42px").add("borderLeft", "2px dotted #000;").z0.$} />
          </div>
        </div>
      </PageContent>
    </>
  );
}

type MilestoneRowProps = {
  milestone: SchedulePhaseOrPlanMilestone;
  firstNotStarted: boolean;
  onClick: () => void;
  isLegacy: boolean;
};

function MilestoneRow(props: MilestoneRowProps) {
  const { milestone, firstNotStarted, onClick, isLegacy } = props;
  const statusDisplay = getStatusDisplay({ milestone, firstNotStarted });
  const { icon, replacementTitle, name } = getHomeownerConfig(milestone);
  return (
    <Card
      xss={Css.df.px3.py2.aic.z1.bgWhitePure.if(!isLegacy).cursor("default").$}
      onClick={isLegacy ? onClick : undefined}
    >
      <div css={Css.wPx(40).hPx(40).br100.bgTaupe.df.aic.jcc.$}>{icon}</div>
      <div css={Css.header16.fw4.ml2.mra.$}>{replacementTitle || name}</div>

      <div
        css={{
          ...Css.px1.py("4px").add("fontSize", "12px").fw7.lh("14px").br4.nowrap.$,
          ...statusDisplay.css,
        }}
      >
        {statusDisplay.title}
      </div>
    </Card>
  );
}
